import { useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';

export const useKeyboardShortcuts = (actions = {}) => {
  const { toggleTheme } = useTheme();

  const handleKeyPress = useCallback((event) => {
    // Ne pas intercepter si l'utilisateur tape dans un input, textarea ou select
    const activeElement = document.activeElement;
    const isInputActive = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      activeElement.isContentEditable
    );

    // Vérifier les modificateurs
    const isCtrl = event.ctrlKey || event.metaKey; // Ctrl sur Windows/Linux, Cmd sur Mac
    const isShift = event.shiftKey;
    const isAlt = event.altKey;

    // Raccourcis globaux (fonctionnent partout)
    if (isCtrl && !isShift && !isAlt) {
      switch (event.key.toLowerCase()) {
        case 's':
          event.preventDefault();
          if (actions.onSave) {
            actions.onSave();
            showShortcutNotification('💾 Formulaire sauvegardé');
          }
          return;
        
        case 'p':
          event.preventDefault();
          if (actions.onPrint) {
            actions.onPrint();
            showShortcutNotification('🖨️ Impression lancée');
          }
          return;
        
        case 'e':
          event.preventDefault();
          if (actions.onExportPDF) {
            actions.onExportPDF();
            showShortcutNotification('📄 Export PDF lancé');
          }
          return;
        
        case 'n':
          event.preventDefault();
          if (actions.onNewForm) {
            actions.onNewForm();
            showShortcutNotification('📝 Nouveau formulaire');
          }
          return;
        
        case 't':
          event.preventDefault();
          if (actions.onOpenTemplates) {
            actions.onOpenTemplates();
            showShortcutNotification('📋 Templates ouverts');
          }
          return;
        
        case 'd':
          event.preventDefault();
          if (actions.onGoToDashboard) {
            actions.onGoToDashboard();
            showShortcutNotification('📊 Redirection vers Dashboard');
          }
          return;
      }
    }

    // Raccourcis avec Ctrl+Shift
    if (isCtrl && isShift && !isAlt) {
      switch (event.key.toLowerCase()) {
        case 'a':
          event.preventDefault();
          if (actions.onGoToApprovals) {
            actions.onGoToApprovals();
            showShortcutNotification('✅ Redirection vers Approbations');
          }
          return;
        
        case 't':
          event.preventDefault();
          toggleTheme();
          showShortcutNotification('🎨 Thème changé');
          return;
        
        case 'f':
          event.preventDefault();
          if (actions.onToggleFishbone) {
            actions.onToggleFishbone();
            showShortcutNotification('🐟 Diagramme Fishbone basculé');
          }
          return;
      }
    }

    // Raccourcis avec Alt (ne fonctionnent que hors des inputs)
    if (isAlt && !isCtrl && !isShift && !isInputActive) {
      switch (event.key.toLowerCase()) {
        case 'h':
          event.preventDefault();
          showKeyboardShortcutsHelp();
          return;
        
        case 'f':
          event.preventDefault();
          // Focus sur le premier champ du formulaire
          const firstField = document.querySelector('.field-input');
          if (firstField) {
            firstField.focus();
            showShortcutNotification('🎯 Focus sur le premier champ');
          }
          return;
      }
    }

    // Échap pour fermer les modales/panels
    if (event.key === 'Escape' && !isCtrl && !isShift && !isAlt) {
      if (actions.onEscape) {
        actions.onEscape();
        showShortcutNotification('❌ Échap pressé');
      }
    }

    // F5 pour actualiser (laisser le comportement par défaut mais notifier)
    if (event.key === 'F5' && !isCtrl && !isShift && !isAlt) {
      showShortcutNotification('🔄 Actualisation de la page');
    }

  }, [actions, toggleTheme]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  return {
    // Retourner des informations utiles si nécessaire
    shortcuts: getShortcutsList()
  };
};

// Fonction pour afficher une notification temporaire des raccourcis
const showShortcutNotification = (message) => {
  // Supprimer toute notification existante
  const existingNotification = document.querySelector('.shortcut-notification');
  if (existingNotification) {
    existingNotification.remove();
  }

  // Créer une nouvelle notification
  const notification = document.createElement('div');
  notification.className = 'shortcut-notification';
  notification.textContent = message;
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: var(--primary-color);
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    animation: slideInRight 0.3s ease-out;
    max-width: 300px;
  `;

  // Ajouter l'animation CSS
  if (!document.querySelector('#shortcut-animations')) {
    const style = document.createElement('style');
    style.id = 'shortcut-animations';
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(notification);

  // Supprimer après 2 secondes
  setTimeout(() => {
    if (notification && notification.parentNode) {
      notification.style.animation = 'slideOutRight 0.3s ease-in forwards';
      setTimeout(() => {
        if (notification && notification.parentNode) {
          notification.remove();
        }
      }, 300);
    }
  }, 2000);
};

// Fonction pour afficher l'aide des raccourcis
const showKeyboardShortcutsHelp = () => {
  const helpModal = document.createElement('div');
  helpModal.className = 'shortcuts-help-modal';
  helpModal.innerHTML = `
    <div class="shortcuts-help-content">
      <div class="shortcuts-help-header">
        <h3>⌨️ Raccourcis Clavier</h3>
        <button class="shortcuts-help-close" onclick="this.closest('.shortcuts-help-modal').remove()">×</button>
      </div>
      <div class="shortcuts-help-body">
        <div class="shortcuts-section">
          <h4>🔥 Actions Principales</h4>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>S</kbd>
            <span>Sauvegarder le formulaire</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>P</kbd>
            <span>Imprimer</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>E</kbd>
            <span>Exporter en PDF</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>N</kbd>
            <span>Nouveau formulaire</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>T</kbd>
            <span>Ouvrir les templates</span>
          </div>
        </div>
        
        <div class="shortcuts-section">
          <h4>🧭 Navigation</h4>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>D</kbd>
            <span>Aller au Dashboard</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>A</kbd>
            <span>Aller aux Approbations</span>
          </div>
          <div class="shortcut-item">
            <kbd>Alt</kbd> + <kbd>F</kbd>
            <span>Focus premier champ</span>
          </div>
        </div>
        
        <div class="shortcuts-section">
          <h4>🎨 Interface</h4>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>T</kbd>
            <span>Changer le thème</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>F</kbd>
            <span>Basculer Fishbone</span>
          </div>
          <div class="shortcut-item">
            <kbd>Échap</kbd>
            <span>Fermer les modales</span>
          </div>
        </div>
        
        <div class="shortcuts-section">
          <h4>🆘 Aide</h4>
          <div class="shortcut-item">
            <kbd>Alt</kbd> + <kbd>H</kbd>
            <span>Afficher cette aide</span>
          </div>
        </div>
      </div>
    </div>
    <div class="shortcuts-help-overlay" onclick="this.closest('.shortcuts-help-modal').remove()"></div>
  `;

  // Ajouter les styles pour la modale
  if (!document.querySelector('#shortcuts-help-styles')) {
    const style = document.createElement('style');
    style.id = 'shortcuts-help-styles';
    style.textContent = `
      .shortcuts-help-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .shortcuts-help-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
      }
      
      .shortcuts-help-content {
        position: relative;
        background: var(--surface-elevated);
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        max-width: 600px;
        max-height: 80vh;
        overflow-y: auto;
        color: var(--text-color);
      }
      
      .shortcuts-help-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 24px 16px;
        border-bottom: 1px solid var(--border-color);
      }
      
      .shortcuts-help-header h3 {
        margin: 0;
        font-size: 20px;
        color: var(--text-color);
      }
      
      .shortcuts-help-close {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: var(--text-secondary);
        padding: 4px 8px;
        border-radius: 4px;
      }
      
      .shortcuts-help-close:hover {
        background-color: var(--hover-color);
      }
      
      .shortcuts-help-body {
        padding: 20px 24px;
      }
      
      .shortcuts-section {
        margin-bottom: 24px;
      }
      
      .shortcuts-section h4 {
        margin: 0 0 12px 0;
        font-size: 16px;
        color: var(--primary-color);
        border-bottom: 1px solid var(--border-light);
        padding-bottom: 4px;
      }
      
      .shortcut-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid var(--border-light);
      }
      
      .shortcut-item:last-child {
        border-bottom: none;
      }
      
      .shortcut-item kbd {
        background: var(--surface-color);
        border: 1px solid var(--border-color);
        border-radius: 4px;
        padding: 2px 6px;
        font-size: 12px;
        font-family: monospace;
        margin: 0 2px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }
      
      .shortcut-item span {
        color: var(--text-secondary);
        font-size: 14px;
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(helpModal);
};

// Liste des raccourcis disponibles
const getShortcutsList = () => {
  return {
    save: 'Ctrl + S',
    print: 'Ctrl + P',
    exportPDF: 'Ctrl + E',
    newForm: 'Ctrl + N',
    openTemplates: 'Ctrl + T',
    dashboard: 'Ctrl + D',
    approvals: 'Ctrl + Shift + A',
    toggleTheme: 'Ctrl + Shift + T',
    toggleFishbone: 'Ctrl + Shift + F',
    focusFirstField: 'Alt + F',
    help: 'Alt + H',
    escape: 'Échap'
  };
};
